import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import { Case } from 'dashboard/models/case';
import { CaseState } from 'dashboard/models/case-template';
import { ModuleChecklist } from 'dashboard/modules/Checklist/model';
import Placeholder from 'dashboard/modules/Placeholder';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import { Paragraph } from 'design-system/components/Text';

const ItemList = styled('div')`
  display: grid;
  grid-template-columns: auto;
  row-gap: ${Spacing[2]};
`;

const ChecklistItemContainer = styled.label`
  cursor: pointer;
  :hover {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const StickerGroup = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[1]};
`;

const CheckboxContainer = styled('div')`
  display: grid;
  grid-template-columns: 16px auto;
  column-gap: ${Spacing[2]};

  padding: 2px;
`;

const RequirementContainer = styled('div')`
  display: grid;
  column-gap: ${Spacing[2]};
  grid-template-columns: 76px auto;

  align-items: center;
`;

interface ChecklistProps {
  module: ModuleChecklist;
  states: CaseState[];
  case: Case;
  isPrint?: boolean;
  handleToggleChecklistItem: (itemId: string) => void;
}

const Checklist = ({
  module,
  states,
  case: kase,
  isPrint = false,
  handleToggleChecklistItem,
}: ChecklistProps) => {
  const checklistItems = module.items.map((itemId: string) => {
    const checklistItemIdx = kase.attributes['checklist-items'].findIndex(
      (item) => item.token === itemId,
    );

    if (checklistItemIdx === -1) {
      // If this case doesn't know about the checklist item, don't show it.
      return null;
    }
    const checklistItem = kase.attributes['checklist-items'][checklistItemIdx];
    const isUnchecked = checklistItem['completed-at'] == null;

    const isRequiredFor = checklistItem['required-for'].length > 0;
    const requiredStates = (
      <RequirementContainer>
        <Paragraph size='sm'>Required for:</Paragraph>
        <StickerGroup data-test={`checklist-module__checklist-item__${checklistItem.token}-states`}>
          {checklistItem['required-for'].map((stateId) => {
            const caseState = states.find(
              (state: CaseState) => state.token === stateId && !state['archived-at'],
            );
            return (
              <>
                {caseState && (
                  <CaseStatusSticker
                    key={`${checklistItem.description} - ${stateId}`}
                    data-test={`checklist-module__checklist-item__${checklistItem.token}--${stateId}`}
                    caseState={caseState}
                  />
                )}
              </>
            );
          })}
        </StickerGroup>
      </RequirementContainer>
    );

    return (
      <ChecklistItemContainer
        key={`${module.token} - ${checklistItem.token}`}
        id={checklistItem.token}
        htmlFor={`checkbox--${checklistItem.token}`}
        data-test={`checklist-module__checklist-item--${checklistItem.token}`}
      >
        <CheckboxContainer>
          <Checkbox
            id={`checkbox--${checklistItem.token}`}
            name={checklistItem.token}
            checked={!isUnchecked}
            onChange={() => {
              if (isPrint) return;

              handleToggleChecklistItem(checklistItem.token);
            }}
          />
          <div>{checklistItem.description}</div>

          <div>{/* Empty div to line up with the checkbox */}</div>
          <div>{isRequiredFor && requiredStates}</div>
        </CheckboxContainer>
      </ChecklistItemContainer>
    );
  });
  return checklistItems.length > 0 ? (
    <Card data-test={`module__case-checklist--${module.token}`}>
      <Card.Body>
        <Card.Title>{module.title}</Card.Title>
        <ItemList>{checklistItems}</ItemList>
      </Card.Body>
    </Card>
  ) : (
    <Placeholder title='Checklist module' message='No items configured' />
  );
};

export default Checklist;
